import { useEffect, useState } from "react";
import Api from "../../../Api";
import DatesRow from "../users_planning/DatesRow";
import ProjectsBlock from "./ProjectsBlock";
import { useTeambookFilter } from "../../../stores/planner";
import { useTranslation } from "react-i18next";
import { calculateProjectsFilters } from "../lib/calculateFilters";
import { compareStrings } from "../../../lib/StringFormat";
import { useCapacityStore } from "../../../stores/capacity";
import { shallow } from "zustand/shallow";
import NoBookingsFiltered from "../../planner/default_components/NoBookingsFiltered";
import PropTypes from "prop-types";
import lsKeys from "../../default_values/defaultKeys";

const ProjectPlanning = ({ date, allProjects, users, clients, jumpToUser, selectedProject, selectedViewType }) => {
  const { filterValues, filterType } = useTeambookFilter();
  const { t } = useTranslation();

  const [projects, setProjects] = useState(null);
  const [setProjectReservations, capacityReservations, setCapacityReservations] = useCapacityStore(
    (state) => [state.setProjectCapacity, state.userCapacity, state.setUserCapacity],
    shallow
  );
  const [shownUsers, setShownUsers] = useState([]);
  const [userOrder, setUserOrder] = useState(
    localStorage.getItem(lsKeys.PROJECTS_CAPACITY_SORTING_ORDER) || "project_name_a"
  );

  const [showTimeOffs] = useCapacityStore((state) => [state.showTimeOffs], shallow);
  const [emptyFiltered, setEmptyFiltered] = useState(false);

  useEffect(() => {
    if (projects) {
      setEmptyFiltered(projects.length === 0 && filterValues.length > 0);
    }
  }, [projects]);

  useEffect(() => {
    setShownUsers(users.filter((user) => user.team_ids.length > 0));

    if (users.length > 0) {
      Api.CapacityReservations.get({
        user_ids: users.filter((user) => user.team_ids.length > 0).map((u) => u.id),
        start_date: date.toISODate(),
        end_date: date.plus({ months: selectedViewType }).toISODate(),
      }).then((res) => {
        setCapacityReservations(res.data);
      });
    }

    if (allProjects.length > 0) {
      let projs = allProjects
        .filter((p) => p.kind !== "time_off")
        .filter((p) => p.active && p.capacity_counted)
        .sort((x, y) => compareStrings(x.name, y.name));
      let timeOffs = allProjects
        .filter((p) => p.kind === "time_off")
        .filter((p) => p.active && p.capacity_counted)
        .sort((x, y) => compareStrings(x.name, y.name));

      setProjects([...projs, ...timeOffs]);

      Api.CapacityReservations.get_project_reservations({
        project_ids: [...projs, ...timeOffs].map((p) => p.id),
        start_date: date.toISODate(),
        end_date: date.plus({ months: selectedViewType }).toISODate(),
      }).then((res) => {
        setProjectReservations(res.data);
      });
    }
  }, [date, users, allProjects]);

  useEffect(() => {
    if (allProjects.length === 0) return;

    let projs = allProjects.filter((p) => p.kind !== "time_off").sort((x, y) => compareStrings(x.name, y.name));
    let timeOffs = allProjects.filter((p) => p.kind === "time_off").sort((x, y) => compareStrings(x.name, y.name));

    const filteredProjects = calculateProjectsFilters([...projs, ...timeOffs], clients, filterValues, filterType, t);

    if (selectedProject && filteredProjects.find((project) => project.id === selectedProject)) {
      setProjects([
        filteredProjects.filter((project) => project.id === selectedProject)[0],
        ...sortProjectsAlphabetically(filteredProjects.filter((project) => project.id !== selectedProject)),
      ]);
    } else {
      setProjects(sortProjectsAlphabetically(filteredProjects));
    }
  }, [allProjects, filterValues, filterType, selectedProject, userOrder]);

  const sortProjectsAlphabetically = (array) => {
    return [...array].sort((a, b) => {
      if (userOrder === "project_name_a") {
        return a.name > b.name ? 1 : -1;
      } else if (userOrder === "project_name_z") {
        return a.name > b.name ? -1 : 1;
      }
    });
  };

  const changeOrder = (order) => {
    setUserOrder(order);
    localStorage.setItem(lsKeys.PROJECTS_CAPACITY_SORTING_ORDER, order);
  };

  return (
    <div className="capacity__component projects-planning">
      {emptyFiltered ? (
        <NoBookingsFiltered />
      ) : (
        <>
          <DatesRow
            date={date}
            selectedViewType={selectedViewType}
            planningBy="project"
            userOrder={userOrder}
            changeOrder={changeOrder}
          />

          {projects
            ?.filter((pr) => pr.kind !== "time_off" || showTimeOffs)
            .map((project) => (
              <ProjectsBlock
                project={project}
                date={date}
                capacityReservations={capacityReservations}
                setCapacityReservations={setCapacityReservations}
                users={shownUsers}
                jumpToUser={jumpToUser}
                selectedViewType={selectedViewType}
                key={`project-${project.id}`}
                projects={projects?.filter((pr) => pr.kind !== "time_off" || showTimeOffs) || []}
              />
            ))}
        </>
      )}
    </div>
  );
};

ProjectPlanning.propTypes = {
  date: PropTypes.object.isRequired,
  allProjects: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  jumpToUser: PropTypes.func.isRequired,
  selectedProject: PropTypes.number,
  selectedViewType: PropTypes.number.isRequired,
};

export default ProjectPlanning;
